import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";

interface ScrollSentinelProps {
  onIntersect: () => void;
  hasMore: boolean;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  /** Replaces the default spinner and the error and Retry body. */
  children?: ReactNode;
}

/**
 * A scroll sentinel component that triggers a callback when it becomes visible.
 * Used for infinite scroll implementations with IntersectionObserver.
 */
export const ScrollSentinel = ({
  onIntersect,
  hasMore,
  isLoading,
  error,
  onRetry,
  children,
}: ScrollSentinelProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const onIntersectRef = useRef(onIntersect);
  const isLoadingRef = useRef(isLoading);
  const isIntersectingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Fire at most once per "enters view" episode. The sentinel must leave the
  // viewport before it can trigger another page load. Without this, a short
  // list whose sentinel stays visible re-triggers fetchNextPage repeatedly,
  // churning the list (flicker).
  const canFireRef = useRef(true);

  // Keep callback/loading in refs so the observer does NOT get torn down and
  // rebuilt when they change. Recreating the observer on every isLoading toggle
  // was the flicker source: on rebuild it immediately re-fired against a
  // still-visible sentinel.
  useLayoutEffect(() => {
    onIntersectRef.current = onIntersect;
  }, [onIntersect]);

  useLayoutEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Observer is recreated only when the sentinel's presence toggles
  // (hasMore/error control whether the element renders at all) — never on an
  // isLoading flip.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        if (!entry.isIntersecting) {
          // Left the viewport: re-arm for the next episode.
          canFireRef.current = true;
          return;
        }
        if (canFireRef.current && hasMore && !isLoadingRef.current && !error) {
          canFireRef.current = false;
          onIntersectRef.current();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;
    observer.observe(element);
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [hasMore, error]);

  // The observer only fires on intersection CHANGES. If a freshly loaded page
  // does not push the sentinel out of view (the list is still shorter than its
  // scroll container while more remains), it would otherwise never auto-load
  // the rest. When a load settles, force a FRESH geometry check by re-observing
  // rather than trusting the cached `isIntersectingRef` — that ref is only
  // updated asynchronously by the observer callback, so reading it here (before
  // the newly rendered page has been re-measured) yields a stale `true` and
  // cascades page-after-page through the entire list. Re-observing makes the
  // browser deliver a fresh entry computed against the scroll container's real
  // post-layout geometry: if the new page pushed the sentinel out of view,
  // pagination stops; if the list is still short, it fires exactly once more.
  useEffect(() => {
    if (isLoading || !hasMore || error) return;
    const element = ref.current;
    const observer = observerRef.current;
    if (!element || !observer) return;
    canFireRef.current = true;
    observer.unobserve(element);
    observer.observe(element);
  }, [isLoading, hasMore, error]);

  if (!hasMore && !error) return null;

  if (children !== undefined) return <div ref={ref}>{children}</div>;

  return (
    <div ref={ref} className="py-4 flex flex-col justify-center items-center">
      {isLoading && (
        <svg
          className="animate-spin h-5 w-5 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {error && (
        <div className="text-center">
          <p className="text-sm text-red-500 mb-2">Failed to load more</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
};
